/**
 * Categories, one bucket at a time.
 *
 * A category belongs to exactly one bucket — the household, or one business (PRODUCT.md §5) — so
 * the page picks a bucket first and everything below applies to it. That keeps the mental model
 * the same as the one the review screen will use when assigning a line.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Field, Input, Notice, Select } from "@/components/ui/Field";
import { SettingsHeader } from "@/features/settings/SettingsHeader";
import {
  businessesQuery,
  categoriesQuery,
  useCreateCategory,
  useDeleteCategory,
  useRenameCategory,
  type Category,
} from "@/features/settings/queries";

const HOUSEHOLD = "household";

export function CategoriesPage() {
  const { t } = useTranslation();
  const businesses = useQuery(businessesQuery);
  const [bucket, setBucket] = useState<string>(HOUSEHOLD);

  const businessId = bucket === HOUSEHOLD ? null : bucket;
  const categories = useQuery(categoriesQuery(businessId));
  const create = useCreateCategory();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | undefined>();

  async function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(undefined);
    try {
      await create.mutateAsync({ name: trimmed, businessId });
      setName("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("settings.errors.saveFailed"));
    }
  }

  return (
    <section className="space-y-5">
      <SettingsHeader
        title={t("settings.categories.title")}
        description={t("settings.categories.subtitle")}
      />

      <Field name="bucket" label={t("settings.categories.bucket")}>
        <Select id="bucket" value={bucket} onChange={(event) => setBucket(event.target.value)}>
          <option value={HOUSEHOLD}>{t("settings.categories.household")}</option>
          {businesses.data?.map((business) => (
            <option key={business.id} value={business.id}>
              {business.name}
            </option>
          ))}
        </Select>
      </Field>

      {categories.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {categories.isError && <Notice tone="error">{t("settings.errors.loadFailed")}</Notice>}

      {categories.data && categories.data.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {categories.data.map((category) => (
            <CategoryRow key={category.id} category={category} />
          ))}
        </ul>
      )}
      {categories.data?.length === 0 && (
        <Notice tone="info">{t("settings.categories.empty")}</Notice>
      )}

      <div className="space-y-3">
        <Field name="newCategory" label={t("settings.categories.addLabel")} error={error}>
          <Input
            id="newCategory"
            value={name}
            placeholder={t("settings.categories.addPlaceholder")}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void add();
              }
            }}
          />
        </Field>
        <Button
          type="button"
          disabled={create.isPending || !name.trim()}
          onClick={() => void add()}
        >
          {create.isPending ? t("common.saving") : t("settings.categories.add")}
        </Button>
      </div>
    </section>
  );
}

function CategoryRow({ category }: { category: Category }) {
  const { t } = useTranslation();
  const rename = useRenameCategory();
  const remove = useDeleteCategory();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [error, setError] = useState<string | undefined>();

  async function save() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === category.name) {
      setEditing(false);
      setName(category.name);
      return;
    }
    setError(undefined);
    try {
      await rename.mutateAsync({ id: category.id, name: trimmed });
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("settings.errors.saveFailed"));
    }
  }

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex items-center gap-3">
        {editing ? (
          <Input
            value={name}
            autoFocus
            aria-label={t("settings.categories.nameOf", { name: category.name })}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void save();
              if (event.key === "Escape") {
                setEditing(false);
                setName(category.name);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="flex-1 text-left font-medium"
            onClick={() => setEditing(true)}
          >
            {category.name}
          </button>
        )}
        {editing ? (
          <Button type="button" className="w-auto px-3" onClick={() => void save()}>
            {t("common.save")}
          </Button>
        ) : (
          <button
            type="button"
            aria-label={t("settings.categories.deleteOf", { name: category.name })}
            disabled={remove.isPending}
            className="rounded-md p-2 text-stone-400 hover:bg-red-50 hover:text-red-700"
            onClick={() => remove.mutate(category.id)}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      {error && <Notice tone="error">{error}</Notice>}
    </li>
  );
}
